import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { useAppT, localizeNotification } from "./home/i18n";
import { Bell, Check } from "lucide-react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const { a } = useAppT();
  const ref = useRef();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await API.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread_count);
    } catch { /* guest or offline */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    try { if (!n.read) await API.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => { try { await API.post("/notifications/read-all"); } catch { /* ignore */ } load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={() => setOpen((o) => !o)} className="relative rounded-full p-2 text-[#5C5855] hover:bg-[#1F4A38]/6">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#8B3A3A] px-1 text-[10px] font-bold text-white">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-panel" className="absolute right-0 z-50 mt-2 w-80 rounded-md border border-[#E8E4DB] bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-[#E8E4DB] px-3 py-2">
            <span className="text-sm font-semibold text-[#173A2C]">{a.notif.title}</span>
            {unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="inline-flex items-center gap-1 text-[11px] text-[#1F4A38]"><Check className="h-3 w-3" /> {a.notif.markAll}</button>}
          </div>
          <div className="max-h-96 overflow-auto">
            {items.length === 0 && <div className="px-3 py-6 text-center text-sm text-[#8a8580]">{a.notif.empty}</div>}
            {items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className={`block w-full border-b border-[#F1EEE7] px-3 py-2.5 text-left hover:bg-[#FDFBF7] ${n.read ? "" : "bg-[#C4A46B]/8"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#C4A46B]" />}
                  {(() => { const ln = localizeNotification(n, a); return (
                  <div className={n.read ? "pl-4" : ""}>
                    <div className="text-sm font-medium text-[#2C2A29]">{ln.title}</div>
                    {ln.body && <div className="text-xs text-[#5C5855]">{ln.body}</div>}
                    <div className="mt-0.5 text-[10px] text-[#8a8580]">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                  </div>
                  ); })()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
